//-- Copyright (c) 2021 TMT Insights Group, LLC
import { useEffect, useState } from 'react';
import { TableCell, TableHeadProps, Tooltip } from '@mui/material';
import styled from '@emotion/styled';
import { useAppSelector } from '@/redux/hooks';
import { TableColumn } from '@/redux/slices/slice_types';
import { selectDataTableIsMultiSort, selectDataTableMultiSortById, selectDataTableSorting } from '@/redux/selectors/dataTableSelectors';
import { SortOrder } from '@/components/_common/Table/sortOrders';
import TableHead from '@/components/_common/Table/TableHead';
import { Overlay } from '@/components/_common/Table/TableRow';
import PolarisIcon from '@/components/_common/PolarisIcon';

type TableHeadSortableProps = TableHeadProps & {
  columns: TableColumn[];
  onSort?: (column: string) => void;
  scrollable: boolean;
  scrollEnd: 'left' | 'right' | null;
};

const TableHeadSortable = ({ columns, onSort, scrollable, scrollEnd, sx, ...props }: TableHeadSortableProps) => {
  const [visibleCols, setVisibleCols] = useState(columns);

  useEffect(() => {
    setVisibleCols(columns.filter((col) => !col.hidden && !col.omitted));
  }, [columns]);

  return (
    <StyledTableHead {...props} className="table-head-sortable" sx={sx}>
      {visibleCols.map((col, i) => {
        return <HeaderCell key={`cell-${i}`} col={col} i={i} scrollable={scrollable} scrollEnd={scrollEnd} onSort={onSort} />;
      })}
    </StyledTableHead>
  );
};
export default TableHeadSortable;

type HeaderCellProps = {
  col: TableColumn;
  i: number;
  scrollable: boolean;
  scrollEnd: 'left' | 'right' | null;
  onSort?: (column: string) => void;
};

const HeaderCell = ({ col, scrollable, scrollEnd, onSort }: HeaderCellProps) => {
  const isMultiSort = useAppSelector(selectDataTableIsMultiSort);
  const sorting = useAppSelector(selectDataTableSorting);
  const { order, id, sortOrder } = isMultiSort
    ? useAppSelector(selectDataTableMultiSortById(col?.sortField || col.id))
    : useAppSelector(selectDataTableSorting);

  return (
    <StyledCell
      id={col.id}
      style={{
        whiteSpace: 'nowrap',
        minWidth: col.minWidth,
        maxWidth: col.maxWidth,
        width: col.width,
        position: col.id === 'priority' && scrollable && scrollEnd !== 'left' ? 'sticky' : undefined
      }}
      draggable={col.isExpandable || col.isOverflow || col.isCheckbox ? false : true}
    >
      {(col.id === 'expandable' || col.id === 'priority') && scrollable && scrollEnd !== 'left' && <Overlay position={'right'} header />}
      <CellLabel className={col.isCheckbox ? 'cell-checkbox' : undefined}>{col.label}</CellLabel>
      {!!col.tooltip && (
        <TooltipWrapped title={col.tooltip.title} placement={col.tooltip.placement} arrow>
          <TooltipIcon icon="info" />
        </TooltipWrapped>
      )}
      {onSort && !col.disableSort && col.label && (
        <SortWrapper>
          <SortIcon
            icon={order === SortOrder.Ascending ? 'up' : 'down'}
            active={+!!(id === (col.sortField || col.id) && order)}
            height={15}
            onClick={() => onSort(col.sortField || col.id)}
          />
          {isMultiSort && sortOrder !== undefined && sorting.length > 1 && <MultiSortOrder>{sortOrder + 1}</MultiSortOrder>}
        </SortWrapper>
      )}
      {col.id === 'overflow' && scrollable && scrollEnd !== 'right' && <Overlay position={'left'} header />}
    </StyledCell>
  );
};

const StyledCell = styled(TableCell)`
  white-space: nowrap;

  &:first-of-type {
    .cell-checkbox {
      height: unset;
      line-height: unset;
    }
  }
`;

const StyledTableHead = styled(TableHead)`
  background: ${({ theme }) => theme.palette.background.default};
  position: relative;

  & th:hover svg {
    opacity: 1;
    transition: ease opacity 500ms;
  }
`;

const SortWrapper = styled.div`
  display: inline-block;
  position: relative;
`;

const SortIcon = styled(PolarisIcon)<{ active: number }>`
  vertical-align: middle;
  margin-left: ${({ theme }) => theme.spacing(0.5)};
  opacity: ${({ active }) => (active ? 1 : 0)};
  color: ${({ theme }) => theme.palette.primary.main};
  transition: ease opacity 500ms;

  &:hover {
    cursor: pointer;
  }
`;
const MultiSortOrder = styled.div`
  position: absolute;
  top: 0;
  left: 100%;
  transform: translateY(-20%);
  color: ${({ theme }) => theme.palette.primary.main};
  font-size: 0.7rem;
`;

const CellLabel = styled.span`
  position: relative;
  opacity: 1;
  height: 20px;
  line-height: 20px;
  display: inline-block;
  transition: ease opacity 200ms;
`;

const TooltipIcon = styled(PolarisIcon)`
  vertical-align: middle;
  margin-left: ${({ theme }) => theme.spacing(0.5)};
  margin-top: -2px;
  width: 12px;
  opacity: 0.8;
`;

const TooltipWrapped = styled((props: any) => <Tooltip classes={{ popper: props.className }} {...props} />)`
  & .MuiTooltip-tooltip {
    line-height: 1.25rem;
    max-width: 200px;
    text-align: center;
  }
`;
